import React, { useContext, useEffect, useState } from 'react';

import { createInstance, OptimizelyProvider, setLogLevel } from '@optimizely/react-sdk';
import { getConfig } from '@services/app.config.loader';
import { getBrand } from '@helpers/brand.utils';
import ProfileContext from '@context/ProfileContext';

export type FEOptimizelyContextType = {
  isReady: boolean;
  getFeatureToggle: any;
  isFeatureToggleOn: any;
  getFeatureToggleList: any;
  getFeatureVariables: any;
};

const DEFAULT_FEOptimizelyContext: FEOptimizelyContextType = {
  isReady: false,
  getFeatureToggle: () => {},
  getFeatureToggleList: () => {},
  isFeatureToggleOn: () => {},
  getFeatureVariables: () => {},
};

export interface TradeServiceFeeVariables {
  enabled?: boolean;
  available_service_fees?: string;
  default_service_fee?: string;
}

export const getTradeServiceFeeVariables = (
  feoptimizely: FEOptimizelyContextType,
  brand: string,
): TradeServiceFeeVariables => {
  return feoptimizely.getFeatureVariables(FEFeatures.TradeServiceFeeEnabled, {
    brand,
  }) as TradeServiceFeeVariables;
};

const FEOptimizelyContext = React.createContext(DEFAULT_FEOptimizelyContext);

export enum FEFeatures {
  BuyerPayEnabled = 'buyerpayenabled',
  PlanCoverageMFEEnabled = 'plancoveragesmfeenabled',
  PropertyEligibilityEndpointEnabled = 'propertyeligibilityendpointenabled',
  RetrieveOrderEndpointEnabled = 'retrieveorderendpointenabled',
  PromoCodeInputEnabled = 'promocodeinputenabled',
  TradeServiceFeeEnabled = 'tradeservicefee',
}

export const FEOptimizelyProvider = ({ children }) => {
  const defaultOptOptions = {
    sdkKey: getConfig('optimizelyKey'),
    eventBatchSize: 10,
    eventFlushInterval: 1000,
    datafileOptions: { urlTemplate: 'https://cdn.optimizely.frontdoorhome.com/datafiles/%s.json' },
  };

  const [optimizelyInstance, setOptimizelyInstance] = useState(createInstance(defaultOptOptions));
  const [onReadyPromise, setOnReadyPromise] = useState(optimizelyInstance.onReady());
  const [isReady, setReady] = useState(false);
  const [attributes, setAttributes] = useState({});

  const { profile } = useContext(ProfileContext);

  //just in case optimizely didn't initialize properly
  useEffect(() => {
    if (profile && optimizelyInstance === undefined) {
      console.log('optimizelyInstance is null, creating new');
      const optimizelyInstance = createInstance(defaultOptOptions);
      setOptimizelyInstance(optimizelyInstance);
      setOnReadyPromise(optimizelyInstance.onReady());
    }
    const attributes = {
      roleIdType: profile?.roleIDType ? profile?.roleIDType : '',
      brand: getBrand(),
      url: window.location.href,
    };
    setAttributes(attributes);
  }, [profile, optimizelyInstance]);

  useEffect(() => {
    setLogLevel('warn');
    setReady(true);
  }, [onReadyPromise]);

  const getFeatureVariables = (key, userAttributes = {}) => {
    if (!userAttributes || Object.keys(userAttributes).length === 0) {
      return optimizelyInstance.decide(key).variables;
    }
    return optimizelyInstance.decide(key, undefined, undefined, {
      ...attributes,
      ...userAttributes,
    }).variables;
  };

  const getFeatureToggle = (key, userAttributes = {}) => {
    if (!userAttributes || Object.keys(userAttributes).length === 0) {
      return optimizelyInstance.decide(key).variationKey;
    }
    return optimizelyInstance.decide(key, undefined, undefined, {
      ...attributes,
      ...userAttributes,
    }).variationKey;
  };

  const isFeatureToggleOn = (key, userAttributes = {}) => {
    if (!isReady) {
      return false;
    }
    return getFeatureToggle(key, userAttributes) === 'on';
  };

  const getFeatureToggleList = () => {
    return Object.keys(optimizelyInstance.getOptimizelyConfig().featuresMap).sort();
  };

  if (!isReady) {
    return <div>{children}</div>;
  }

  return (
    <div>
      {isReady && (
        <OptimizelyProvider
          optimizely={optimizelyInstance}
          user={{
            id: profile?.profileID ? profile.profileID : '',
            attributes: attributes,
          }}
        >
          <FEOptimizelyContext.Provider
            value={{
              isReady,
              getFeatureToggle,
              isFeatureToggleOn,
              getFeatureToggleList,
              getFeatureVariables,
            }}
          >
            {children}
          </FEOptimizelyContext.Provider>
        </OptimizelyProvider>
      )}
    </div>
  );
};

const useOptimizelyFE: any = () => {
  const optimizely = useContext(FEOptimizelyContext);
  return optimizely;
};

export default FEOptimizelyContext;
export { FEOptimizelyContext, useOptimizelyFE };
